import type { AcademyState } from '../types'
import type { AuthUser } from '../context/AuthContext'
import { apiBase } from './backend'

type ApiEnvelope<T> = { ok: true; data: T; csrf?: string } | { ok: false; error: string }
let csrfToken: string | null = null

async function request<T>(action: string, body?: Record<string, unknown>): Promise<T> {
  const response = await fetch(`${apiBase}?action=${encodeURIComponent(action)}`, {
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  let payload: ApiEnvelope<T>
  try {
    payload = await response.json() as ApiEnvelope<T>
  } catch (cause) {
    throw new Error('El servidor de la academia no devolvió una respuesta válida.', { cause })
  }
  if (!response.ok || !payload.ok) throw new Error(payload.ok ? `Error ${response.status}` : payload.error)
  if (payload.csrf) csrfToken = payload.csrf
  return payload.data
}

export const ionosAuth = {
  session: () => request<{ user: AuthUser | null }>('session'),
  bootstrapAdmin: (name: string, email: string, password: string, setupKey: string) => request<{ user: AuthUser }>('bootstrap_admin', { name, email, password, setupKey }),
  login: (email: string, password: string) => request<{ user: AuthUser }>('login', { email, password }),
  register: (name: string, email: string, password: string) => request<{ user: AuthUser }>('register', { name, email, password }),
  resetPassword: (email: string) => request<{ message: string }>('reset_password', { email }),
  updatePassword: (password: string, token?: string) => request<{ message: string }>('update_password', { password, token: token ?? '' }),
  logout: () => request<{ message: string }>('logout', {}),
}

export const loadIonosAcademyState = () => request<AcademyState>('state')

const mutate = (action: string, body: Record<string, unknown> = {}) => request<{ message: string }>(action, body).then(() => undefined)

async function uploadLessonFile(lessonId: string, kind: 'video' | 'resource', file: File, title = ''): Promise<void> {
  const data = new FormData()
  data.set('lessonId', lessonId)
  data.set('kind', kind)
  data.set('title', title)
  data.set('file', file)
  const response = await fetch('/api/upload.php', {
    method: 'POST', credentials: 'same-origin',
    headers: { Accept: 'application/json', ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}) },
    body: data,
  })
  const payload = await response.json() as ApiEnvelope<{ message: string }>
  if (!response.ok || !payload.ok) throw new Error(payload.ok ? `Error ${response.status}` : payload.error)
  if (payload.csrf) csrfToken = payload.csrf
}

export const ionosAcademy = {
  completeLesson: (lessonId: string) => mutate('complete_lesson', { lessonId }),
  submitQuiz: (courseId: string, answers: Record<string, number>) => request<{ score: number }>('submit_quiz', { courseId, answers }).then((result) => result.score),
  createPost: (category: string, title: string, body: string) => mutate('create_post', { category, title, body }),
  editPost: (postId: string, title: string, body: string) => mutate('edit_post', { postId, title, body }),
  deletePost: (postId: string) => mutate('delete_post', { postId }),
  reactToPost: (postId: string) => mutate('react_post', { postId }),
  commentOnPost: (postId: string, body: string) => mutate('comment_post', { postId, body }),
  reportPost: (postId: string) => mutate('report_post', { postId }),
  moderatePost: (postId: string, status: string) => mutate('moderate_post', { postId, status }),
  updateUserStatus: (userId: string, status: string) => mutate('update_user_status', { userId, status }),
  toggleEnrollment: (userId: string, courseId: string) => mutate('toggle_enrollment', { userId, courseId }),
  createCourse: (title: string, description: string, slug: string) => mutate('create_course', { title, description, slug }),
  updateCourse: (courseId: string, title: string, description: string, priceClp: number, salesEnabled: boolean) => mutate('update_course', { courseId, title, description, priceClp, salesEnabled }),
  createCheckout: (courseId: string) => request<{ initPoint: string }>('create_checkout', { courseId }),
  confirmPayment: (paymentId: string) => request<{ enrolled: boolean; status: string }>('confirm_payment', { paymentId }),
  setCourseStatus: (courseId: string, status: string) => mutate('set_course_status', { courseId, status }),
  addModule: (courseId: string, title: string) => mutate('add_module', { courseId, title }),
  addLesson: (moduleId: string, title: string) => mutate('add_lesson', { moduleId, title }),
  updateLesson: (lessonId: string, values: { title: string; summary: string; duration: string; content: string; videoUrl: string }) => mutate('update_lesson', { lessonId, ...values }),
  uploadLessonFile,
  moveModule: (moduleId: string, direction: -1 | 1) => mutate('move_module', { moduleId, direction }),
  moveLesson: (lessonId: string, direction: -1 | 1) => mutate('move_lesson', { lessonId, direction }),
  createQuiz: (courseId: string, title: string) => mutate('create_quiz', { courseId, title }),
  addQuizQuestion: (quizId: string, question: { prompt: string; options: string[]; correctIndex: number; explanation: string }) => mutate('add_quiz_question', { quizId, ...question }),
  publishAnnouncement: (title: string, body: string) => mutate('publish_announcement', { title, body }),
  updateProfile: (values: { name: string; country: string; profession: string; interests: string[] }) => mutate('update_profile', values),
}
